import React, { useEffect, useState, useRef } from "react";
import { db } from "../../firebase";
import { ref, onValue } from "firebase/database";
import "./ChatWindow.css";

export default function ChatWindow({ chatId }) {
  const [mensajes, setMensajes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    text: "",
  });
  const [isBotTyping, setIsBotTyping] = useState(false);

  const chatEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!chatId) return;

    const mensajesRef = ref(db, `chats/${chatId}/historial`);

    const unsubscribe = onValue(mensajesRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setMensajes([]);
        return;
      }

      const lista = Object.keys(data).map((id) => ({
        id,
        ...data[id],
        timestamp: data[id].timestamp
          ? new Date(data[id].timestamp)
          : new Date(),
      }));

      lista.sort((a, b) => a.timestamp - b.timestamp);
      setMensajes(lista);
    });

    return () => unsubscribe();
  }, [chatId]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [mensajes]);

  useEffect(() => {
    if (!chatId) return;

    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setIsBotTyping(true);
        setTimeout(() => setIsBotTyping(false), 3000);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [chatId]);

  const enviarMensaje = async () => {
    if (!nuevoMensaje.trim() || enviando) return;

    setEnviando(true);

    try {
      await fetch(
        "https://nonsyntactic-intestinal-nita.ngrok-free.dev/webhook/panel-mensaje-LaPile",
        {
          method: "POST",
          mode: "no-cors",
          body: JSON.stringify({
            wa_id: chatId,
            mensaje: nuevoMensaje,
          }),
        }
      );

      setNuevoMensaje("");
      setShowEmojiPicker(false);
    } catch (error) {
      console.error("Error enviando mensaje:", error);
    } finally {
      setEnviando(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviarMensaje();
    }
  };

  const insertEmoji = (emoji) => {
    setNuevoMensaje((prev) => prev + emoji);
    inputRef.current?.focus();
  };

  const handleContextMenu = (e, text) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      text,
    });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(contextMenu.text);
    setContextMenu({ visible: false, x: 0, y: 0, text: "" });
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu({ visible: false, x: 0, y: 0, text: "" });
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const flattenMessagesToBubbles = (msgs) => {
    const bubbles = [];

    msgs.forEach((msg) => {
      const timestamp = msg.timestamp;

      if (msg.mensaje_usuario) {
        bubbles.push({
          id: `${msg.id}-usuario`,
          tipo: "usuario",
          texto: msg.mensaje_usuario,
          timestamp,
          orden: 1,
        });
      }

      if (msg.mensaje_bot) {
        bubbles.push({
          id: `${msg.id}-bot`,
          tipo: "bot",
          texto: msg.mensaje_bot,
          timestamp,
          orden: 2,
        });
      }

      if (msg.mensaje_panel) {
        bubbles.push({
          id: `${msg.id}-panel`,
          tipo: "panel",
          texto: msg.mensaje_panel,
          timestamp,
          orden: 3,
        });
      }
    });

    bubbles.sort((a, b) => {
      const diffTime = a.timestamp - b.timestamp;
      if (diffTime !== 0) return diffTime;
      return a.orden - b.orden;
    });

    return bubbles;
  };

  const groupBubblesByDay = (bubbles) => {
    const groups = [];
    let currentDate = null;

    bubbles.forEach((bubble) => {
      const dateStr = bubble.timestamp.toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      if (dateStr !== currentDate) {
        currentDate = dateStr;
        groups.push({ type: "date", date: dateStr });
      }

      groups.push({ type: "bubble", bubble });
    });

    return groups;
  };

  const allBubbles = flattenMessagesToBubbles(mensajes);
  const groupedItems = groupBubblesByDay(allBubbles);

  const renderBubble = (bubble) => {
    let bubbleClass = "bubble";
    let author = "";
    let avatar = "";

    if (bubble.tipo === "bot") {
      bubbleClass += " bubble--bot";
      author = "Bot";
      avatar = "🤖";
    } else if (bubble.tipo === "usuario") {
      bubbleClass += " bubble--user";
      author = "Usuario";
      avatar = "👤";
    } else if (bubble.tipo === "panel") {
      bubbleClass += " bubble--panel";
      author = "Panel";
      avatar = "📋";
    }

    const time = bubble.timestamp.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    return (
      <div
        key={bubble.id}
        className={bubbleClass}
        onContextMenu={(e) => handleContextMenu(e, bubble.texto)}
      >
        <div className="bubble__avatar">{avatar}</div>

        <div className="bubble__content">
          <div className="bubble__author">{author}</div>
          <div className="bubble__text">{bubble.texto}</div>
          <div className="bubble__meta">
            <span className="bubble__time">{time}</span>
            {bubble.tipo === "usuario" && <span className="bubble__check">✓✓</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="chat-window">
      {contextMenu.visible && (
        <div
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button onClick={copyToClipboard}>📋 Copiar mensaje</button>
        </div>
      )}

      <div className="chat-window__messages custom-scroll" ref={messagesContainerRef}>
        {groupedItems.map((item, idx) =>
          item.type === "date" ? (
            <div key={`date-${idx}`} className="date-separator">
              <span>{item.date}</span>
            </div>
          ) : (
            renderBubble(item.bubble)
          )
        )}

        {isBotTyping && (
          <div className="typing-indicator">
            <div className="typing-dot"></div>
            <div className="typing-dot"></div>
            <div className="typing-dot"></div>
            <span>Bot está escribiendo...</span>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      <div className="chat-window__input-area">
        <button
          className="emoji-btn"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          aria-label="Emojis"
        >
          😊
        </button>

        {showEmojiPicker && (
          <div className="emoji-picker">
            <button onClick={() => insertEmoji("😀")}>😀</button>
            <button onClick={() => insertEmoji("😂")}>😂</button>
            <button onClick={() => insertEmoji("❤️")}>❤️</button>
            <button onClick={() => insertEmoji("👍")}>👍</button>
            <button onClick={() => insertEmoji("🎉")}>🎉</button>
            <button onClick={() => insertEmoji("🤖")}>🤖</button>
          </div>
        )}

        <textarea
          ref={inputRef}
          className="chat-input"
          value={nuevoMensaje}
          onChange={(e) => setNuevoMensaje(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribe un mensaje..."
          rows={1}
          disabled={enviando}
          style={{ overflow: "hidden", resize: "none" }}
          onInput={(e) => {
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
          }}
        />

        <button
          className="send-btn"
          onClick={enviarMensaje}
          disabled={enviando || !nuevoMensaje.trim()}
        >
          {enviando ? <span className="spinner-btn"></span> : "📤"}
        </button>
      </div>
    </div>
  );
}